import { useState } from 'react';
import Button from 'components/gruenland/atoms/button/Button';
import Checkbox from 'components/gruenland/atoms/checkbox';
import Input from 'components/gruenland/atoms/input';
import PasswordInput from 'components/gruenland/atoms/input-password';
import Typography from 'components/gruenland/atoms/typography';
import { useFormat } from 'helpers/hooks/useFormat';
import Redirect from 'helpers/redirect';
import { validatePassword } from 'helpers/validation';
import { TYPOGRAPHY_PAGE_TITLE } from 'lib/typography';
import { Reference } from 'types/reference';
import { UseAccountReturn } from 'frontastic/hooks/useAccount/types';
import Feedback from '../../account/account-atoms/feedback';

export interface RegisterFormProps {
  termsOfUseLink?: Reference;
  loggedIn: UseAccountReturn['loggedIn'];
  register: UseAccountReturn['register'];
}

const RegisterForm: React.FC<RegisterFormProps> = ({ loggedIn, register }) => {
  const { formatMessage } = useFormat({ name: 'gruenland' });

  //register data
  const [data, setData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    consentTerms: false,
    consentProfiling: false,
    consentMarketing: false,
  });

  //error
  const [error, setError] = useState('');

  //success
  const [success, setSuccess] = useState('');

  //processing...
  const [loading, setLoading] = useState(false);

  //handle text input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  //handle consent checkbox change
  const handleConsentChange = ({ name, checked }: { name: string; checked: boolean }) => {
    setData({ ...data, [name]: checked });
  };

  //data validation
  const validate = () => {
    const validPassword = validatePassword(data.password);
    const validTerms = data.consentTerms;

    //UI error messages
    if (!validPassword)
      setError(
        formatMessage({
          id: 'validation.password',
          defaultMessage: 'La password deve essere lunga almeno 8 caratteri e contenere almeno una lettera maiuscola.',
        }),
      );

    if (!validTerms)
      setError(
        formatMessage({
          id: 'validation.terms',
          defaultMessage: 'Devi accettare i termini per registrarti.',
        }),
      );

    //return a boolean representing the data validity
    return validPassword && validTerms;
  };

  //form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    //validate data
    if (!validate()) return;
    //processing starts
    setLoading(true);
    //try registering the user with given credentials
    try {
      setError('');
      setSuccess('');
      const response = await register(data);
      if (!response.success) {
        setError(
          response.error?.message ||
            formatMessage({
              id: 'error.createAccount',
              defaultMessage: "Errore durante la creazione dell'account. Riprova più tardi.",
            }),
        );
      } else {
        setSuccess(
          formatMessage({
            id: 'feedback.registrationEmailSent',
            defaultMessage:
              "Ti abbiamo inviato una email di conferma. Clicca sul link nell'email per attivare il tuo account.",
          }),
        );
      }
    } catch (err) {
      setError(
        formatMessage({
          id: 'feedback.wentWrong',
          defaultMessage: 'Qualcosa è andato storto. Riprova più tardi.',
        }),
      );
    }
    //processing ends
    setLoading(false);
  };

  if (loggedIn) return <Redirect target="/account" />;

  // Show success message with home button when registration email is sent
  if (success) {
    return (
      <div className="text-center">
        <Typography as="h3" className={TYPOGRAPHY_PAGE_TITLE}>
          {formatMessage({ id: 'user.registrationSuccess', defaultMessage: 'Registrazione completata!' })}
        </Typography>
        <div className="mt-8 rounded-lg border border-green-200 bg-green-50 p-6">
          <Typography className="text-lg font-medium text-green-800">{success}</Typography>
        </div>
        <Button size="full" className="mt-8" onClick={() => (window.location.href = '/')}>
          {formatMessage({ id: 'common.backToHome', defaultMessage: 'Torna alla home' })}
        </Button>
      </div>
    );
  }

  return (
    <>
      <Typography as="h3" className={TYPOGRAPHY_PAGE_TITLE}>
        {formatMessage({ id: 'user.register', defaultMessage: 'Registrati' })}
      </Typography>
      <form className="mt-10" onSubmit={handleSubmit}>
        <Feedback success={success} error={error} />

        <Input
          id="name"
          name="firstName"
          type="text"
          autoComplete="firstName"
          required
          className="mb-4 md:mb-5"
          label={formatMessage({ id: 'user.firstName', defaultMessage: 'Nome' })}
          onChange={handleChange}
        />

        <Input
          id="name"
          name="lastName"
          type="text"
          autoComplete="lastName"
          required
          className="mb-4 md:mb-5"
          label={formatMessage({ id: 'user.lastName', defaultMessage: 'Cognome' })}
          onChange={handleChange}
        />

        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          className="mb-4 md:mb-5"
          label={formatMessage({ id: 'user.email', defaultMessage: 'Email' })}
          onChange={handleChange}
        />

        <PasswordInput
          required
          id="password"
          name="password"
          autoComplete="current-password"
          label={formatMessage({ id: 'user.password', defaultMessage: 'Password' })}
          className="mb-4 md:mb-5"
          onChange={handleChange}
        />

        <div className="mt-5">
          <Checkbox
            checked={data.consentTerms}
            id="consentTerms"
            name="consentTerms"
            onChange={handleConsentChange}
            label={formatMessage({
              id: 'profile.consentTerms',
              defaultMessage: 'Acconsento ai termini e condizioni',
            })}
            labelPosition="on-right"
            required
          />
          <Checkbox
            checked={data.consentProfiling}
            id="consentProfiling"
            name="consentProfiling"
            onChange={handleConsentChange}
            label={formatMessage({
              id: 'profile.consentProfiling',
              defaultMessage: 'Acconsento al trattamento dei miei dati personali per finalità di profilazione',
            })}
            labelPosition="on-right"
          />
          <Checkbox
            checked={data.consentMarketing}
            id="consentMarketing"
            name="consentMarketing"
            onChange={handleConsentChange}
            label={formatMessage({
              id: 'profile.consentMarketing',
              defaultMessage: 'Acconsento al trattamento dei miei dati personali per finalità di marketing',
            })}
            labelPosition="on-right"
          />
        </div>

        <Button size="full" type="submit" className="mb-4 md:mb-5" disabled={loading}>
          {formatMessage({ id: 'user.register', defaultMessage: 'Registrati' })}
        </Button>
      </form>
    </>
  );
};

export default RegisterForm;
